import { useEffect, useRef, useState } from "react";

const GLYPHS = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789";

// Counts the leading number of a stat up from zero ("10+" → 0…10+). A value
// with no leading number ("USA") decodes letter by letter instead.
export function CountUp({ value, duration = 1600, className }) {
	const ref = useRef(null);
	const [text, setText] = useState(value);

	useEffect(() => {
		setText(value);
		const el = ref.current;
		if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

		const match = /^(\d+)(.*)$/.exec(value);
		let raf = 0;

		const run = () => {
			const t0 = performance.now();
			const tick = (now) => {
				const p = Math.min(1, (now - t0) / duration);
				const eased = 1 - Math.pow(1 - p, 4);
				if (match) {
					setText(`${Math.round(Number(match[1]) * eased)}${match[2]}`);
				} else {
					const settled = Math.floor(eased * value.length);
					setText(
						value
							.split("")
							.map((ch, i) => (i < settled ? ch : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]))
							.join("")
					);
				}
				if (p < 1) raf = requestAnimationFrame(tick);
				else setText(value);
			};
			raf = requestAnimationFrame(tick);
		};

		const io = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					io.disconnect();
					run();
				}
			},
			{ threshold: 0.6 }
		);
		io.observe(el);
		return () => {
			io.disconnect();
			cancelAnimationFrame(raf);
		};
	}, [value, duration]);

	return (
		<span ref={ref} className={className} aria-label={value}>
			<span aria-hidden="true">{text}</span>
		</span>
	);
}
